/**
 * Shared types for admin features.
 * Re-exports GraphQL types and adds admin-specific interfaces.
 */

import {
  type AdminOrder,
  type AdminTicket,
  type OrderStatus,
  TicketStatus,
  type Event,
} from "../../../generated/graphql.ts";

// Re-export for convenience
export type { AdminOrder, AdminTicket, OrderStatus };
export { TicketStatus };

/** Dashboard statistics */
export interface AdminDashboardStats {
  ordersToday: number;
  ticketsIssuedToday: number;
  pendingVerification: number;
}

/** Event with order/ticket stats for admin views */
export interface AdminEventWithStats {
  id: string;
  title: string;
  startsAt: string;
  imageUrl: string | null;
  venue: string | null;
  ordersCount: number;
  ticketsCount: number;
  pendingCount: number;
}

/** Order filter options */
export interface OrdersFilter {
  status?: OrderStatus;
  eventId?: string;
  search?: string;
}

/** Ticket filter options */
export interface TicketsFilter {
  /** Status filter - string to allow empty value */
  status?: string;
  eventId?: string;
  search?: string;
}

/** Ticket validation result */
export interface TicketValidationResult {
  ticket: AdminTicket | null;
  isValid: boolean;
  message: string | null;
  alreadyUsedAt: string | null;
  event: Event | null;
  customerName: string | null;
}

/** Status badge configuration */
export interface StatusConfig {
  label: string;
  color: string;
}

/** Order status labels and colors */
export const ORDER_STATUS_CONFIG: Record<string, StatusConfig> = {
  CREATED: { label: "Создан", color: "bg-slate-100 text-slate-600" },
  AWAITING_PAYMENT: {
    label: "Ожидает оплаты",
    color: "bg-amber-100 text-amber-700",
  },
  PENDING_VERIFICATION: {
    label: "На проверке",
    color: "bg-blue-100 text-blue-700",
  },
  VERIFIED: { label: "Подтверждён", color: "bg-green-100 text-green-700" },
  TICKETS_PENDING_SEND: {
    label: "Ожидает отправки",
    color: "bg-teal-100 text-teal-700",
  },
  TICKET_ISSUED: { label: "Билеты выданы", color: "bg-teal-100 text-teal-700" },
  REJECTED: { label: "Отклонён", color: "bg-red-100 text-red-700" },
  CANCELLED: { label: "Отменён", color: "bg-slate-100 text-slate-500" },
  EXPIRED: { label: "Истёк", color: "bg-slate-100 text-slate-500" },
};

/** Ticket status labels and colors */
export const TICKET_STATUS_CONFIG: Record<string, StatusConfig> = {
  ACTIVE: { label: "Активен", color: "bg-green-100 text-green-700" },
  USED: { label: "Использован", color: "bg-slate-100 text-slate-600" },
  CANCELLED: { label: "Отменён", color: "bg-red-100 text-red-700" },
};

/** Get status config with fallback */
export function getOrderStatusConfig(status: string): StatusConfig {
  return (
    ORDER_STATUS_CONFIG[status] ?? {
      label: status,
      color: "bg-slate-100 text-slate-600",
    }
  );
}

export function getTicketStatusConfig(status: string): StatusConfig {
  return (
    TICKET_STATUS_CONFIG[status] ?? {
      label: status,
      color: "bg-slate-100 text-slate-600",
    }
  );
}

// Re-export getCurrencySymbol from lib/format.ts for convenience
export { getCurrencySymbol } from "../../../lib/format.ts";

/** Format customer name from order */
export function formatCustomerName(customer: {
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  id: string;
}): string {
  const parts = [customer.firstName, customer.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (customer.username) return `@${customer.username}`;
  return `ID: ${customer.id.slice(0, 8)}`;
}
